import {camelCase} from 'lodash'
import queryString from 'query-string'

import {BackendConfig, BackendConfigList} from '../types'

const STORAGE_NAME = `HC-${process.env.REACT_APP_CLIENT_NAME}-Preferred-Backend`

export class BrowserBackendSelector {
  /**
   * @param availableBackends List of backends which should be possible to choose from.
   */
  constructor(availableBackends: BackendConfigList) {
    Object.keys(availableBackends).forEach((name) => {
      availableBackends[name].NAME = name
    })

    this.backends = availableBackends
    this.bundlePreferredBackend = Object.values(availableBackends)[0]

    const parsedQueryString = queryString.parse(window.location.search)
    if (typeof parsedQueryString.backend === 'string') {
      const parsedBackend = parsedQueryString.backend
      const queryPreference = /\d/.exec(parsedBackend)
        ? parsedBackend.toLowerCase()
        : camelCase(parsedBackend)
      if (this.backends[queryPreference]) {
        this.setUserPreferredBackend(queryPreference, false)
      } else {
        console.warn(`Unsupported backend passed by queryString: ${parsedQueryString.backend}!`)
      }
    }
  }

  private backends: BackendConfigList = {}
  private bundlePreferredBackend: BackendConfig

  /**
   * Returns the preferred backend URL based on the current URL the app
   * is running on.
   */
  getLocationBasedPreference(): string {
    if (process.env.REACT_APP_STAGE === 'prod') {
      return 'prod'
    }
    const host = window.location.host

    // e.g. localhost:3000
    if (/^localhost:/.exec(host)) {
      return 'qa'
    }

    // e.g. hub-qa.heidelbergcement.com or hconnecthub-prod.azureedge.net => "qa" or "prod"
    const matchTestCDNDomain = /[a-z]+-(qa|uat|regression|prod|uatr|dptest)\W/.exec(host)
    if (matchTestCDNDomain) {
      // Once CDN is migrated to Azure Front Door - we can replace this with 'origin'
      return matchTestCDNDomain[1]
    }

    // e.g qa.hproduce.digital, uat.hproduce.digital, www.hproduce.digital
    const matchHProduce = /(qa|uat|regression|prod|uatr|dptest|www).hproduce.digital/.exec(host)
    if (matchHProduce) {
      return matchHProduce[1] === 'www' ? 'prod' : matchHProduce[1]
    }

    // e.g. hconnecttmonqa.z6.web.core.windows.net/ => "qa"
    const matchStaticHost = /(qa|uat|uatr|regression|prod)\.z\d+\.web\.core\.windows\.net/.exec(
      host
    )
    if (matchStaticHost) {
      return matchStaticHost[1]
    }
    return 'qa'
  }

  /**
   * Allow the user of the application to select a preferred backend by name.
   *
   * @param backendName Name of the backend to use.
   * @param reload Whether the application should be reloaded after storing the new value.
   */
  setUserPreferredBackend(backendName: string | null, reload = true) {
    if (backendName) {
      localStorage.setItem(STORAGE_NAME, backendName)
    } else {
      localStorage.removeItem(STORAGE_NAME)
    }

    if (reload) {
      window.location.reload()
    }
  }

  /**
   * Returns the user preferred backend by name.
   */
  getUserPreferredBackend(): string | null {
    return localStorage.getItem(STORAGE_NAME) || null
  }

  /**
   * Evaluates the different preference settings to returns the best matching
   * backend configuration.
   */
  getSelectedBackend(): BackendConfig {
    const userPreference = this.getUserPreferredBackend()
    const domainPreference = this.getLocationBasedPreference()
    const backendList = this.backends

    if (userPreference && backendList[userPreference]) {
      return backendList[userPreference]
    }

    if (domainPreference && backendList[domainPreference]) {
      return backendList[domainPreference]
    }

    if (!this.bundlePreferredBackend) {
      throw new Error('No backend available!')
    }

    return this.bundlePreferredBackend
  }
}
